import { useEffect, useState } from "react";
import {
    AlertTriangle, CheckCircle2, Copy, Download, KeyRound, Loader2, Plus, Save, ShieldCheck, Trash2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    useCreateSamlIdp, useDeleteSamlIdp, useSamlIdps, useSpMetadataSummary, useTenants, useUpdateSamlIdp,
    useValidateSamlIdp
} from "@/api/hooks";
import { downloadSpMetadataXml, SAML_PROVIDERS, type SamlIdentityProvider, type SamlProvider } from "@/api/adminBff";
import { useAccessToken } from "@/auth/useAccessToken";
import { EMAIL_CLAIM_DEFAULT, PROVIDER_HINTS } from "./providerPresets";

const CERT_WARNING_DAYS = 90;

export function IdentityProvidersPage() {
    const tenants = useTenants();
    const [tenantId, setTenantId] = useState<string | null>(null);

    // Same pattern as ConnectPoliciesPage: a domain admin's own /api/tenants
    // response only ever contains their own tenant, so this "picker"
    // degenerates to one pre-selected option for them and a real choice for
    // a SuperAdmin browsing across tenants.
    useEffect(() => {
        if (!tenantId && tenants.data && tenants.data.length > 0) {
            setTenantId(tenants.data[0]!.id);
        }
    }, [tenants.data, tenantId]);

    return (
        <div className="mx-auto max-w-6xl">
            <header className="mb-4 flex items-center gap-2">
                <KeyRound className="h-6 w-6 text-primary" />
                <h1 className="text-2xl font-semibold">Identity providers</h1>
            </header>
            <p className="mb-4 text-sm text-muted-foreground">
                Register a tenant's external SAML 2.0 identity provider — Okta, Ping Identity,
                JumpCloud, Azure AD, or any standards-compliant IdP. Once validated and enabled,
                users can sign in through it from the sign-in page.
            </p>

            <div className="mb-4 mt-6">
                <label className="mb-1 block text-xs font-medium">Tenant</label>
                <select
                    value={tenantId ?? ""}
                    onChange={(e) => setTenantId(e.target.value)}
                    className="w-full max-w-sm rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                >
                    <option value="" disabled>Select a tenant…</option>
                    {(tenants.data ?? []).map((t) => (
                        <option key={t.id} value={t.id}>{t.displayName} ({t.slug})</option>
                    ))}
                </select>
            </div>

            {tenantId ? <TenantIdps tenantId={tenantId} /> : null}
        </div>
    );
}

/** Per-registration — each registration has its own ACS URL (see SamlIdentityProvider's class
 * remarks), so this is the specific document to hand to THIS registration's IdP admin. */
function SpMetadataCard({ id }: { id: string }) {
    const summary = useSpMetadataSummary(id);
    const token = useAccessToken();
    const [copied, setCopied] = useState(false);

    const daysLeft = summary.data
        ? Math.floor((new Date(summary.data.certificateExpiresAt).getTime() - Date.now()) / 86_400_000)
        : null;

    const handleCopy = async () => {
        if (!summary.data) return;
        await navigator.clipboard.writeText(summary.data.entityId);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };

    const handleDownload = async () => {
        const xml = await downloadSpMetadataXml(id, token);
        const blob = new Blob([xml], { type: "application/samlmetadata+xml" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "athavita-sp-metadata.xml";
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <section className="rounded-lg border bg-card p-4 shadow-sm">
            <div className="mb-2 flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-muted-foreground" />
                <h2 className="text-sm font-semibold">Your SP metadata</h2>
                <span className="text-xs text-muted-foreground">
                    — hand this to this registration's IdP admin
                </span>
            </div>
            {summary.isPending ? (
                <p className="text-sm text-muted-foreground">Loading…</p>
            ) : summary.data ? (
                <div className="space-y-1 text-sm">
                    <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-muted-foreground">Entity ID</span>
                        <code className="rounded bg-muted px-1.5 py-0.5 text-xs">{summary.data.entityId}</code>
                        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={handleCopy} aria-label="Copy entity ID">
                            {copied ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
                        </Button>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-muted-foreground">ACS URL</span>
                        <code className="rounded bg-muted px-1.5 py-0.5 text-xs">{summary.data.acsUrl}</code>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-muted-foreground">Certificate</span>
                        <code className="rounded bg-muted px-1.5 py-0.5 text-xs">{summary.data.certificateThumbprint}</code>
                        {daysLeft !== null ? (
                            <span className={daysLeft < CERT_WARNING_DAYS ? "text-xs text-amber-600" : "text-xs text-muted-foreground"}>
                                expires in {daysLeft}d
                            </span>
                        ) : null}
                    </div>
                    <Button variant="outline" size="sm" className="mt-2" onClick={handleDownload}>
                        <Download className="h-3.5 w-3.5" /> Download metadata XML
                    </Button>
                </div>
            ) : (
                <p className="text-sm text-destructive">Could not load SP metadata.</p>
            )}
        </section>
    );
}

function TenantIdps({ tenantId }: { tenantId: string }) {
    const idps = useSamlIdps(tenantId);
    const create = useCreateSamlIdp(tenantId);

    const [selectedId, setSelectedId] = useState<string | null>(null);
    const selected = idps.data?.find((p) => p.id === selectedId) ?? null;

    const [newProvider, setNewProvider] = useState<SamlProvider>("Okta");
    const [newDisplayName, setNewDisplayName] = useState("");

    const handleCreate = async () => {
        if (!newDisplayName.trim()) return;
        const created = await create.mutateAsync({
            provider: newProvider,
            displayName: newDisplayName.trim(),
            buttonLabel: newProvider === "Generic" ? "" : `Continue with ${PROVIDER_HINTS[newProvider].label}`,
            metadataUrl: null,
            metadataXml: null,
            emailClaim: EMAIL_CLAIM_DEFAULT,
            autoProvisionUsers: true,
            enabled: false
        });
        setSelectedId(created.id);
        setNewDisplayName("");
    };

    return (
        <div className="space-y-4">
            <section className="rounded-lg border bg-card p-4 shadow-sm">
                <h2 className="mb-3 text-sm font-semibold">New identity provider</h2>
                <div className="grid grid-cols-[160px_1fr_auto] gap-2">
                    <select
                        value={newProvider}
                        onChange={(e) => setNewProvider(e.target.value as SamlProvider)}
                        className="h-9 rounded-md border bg-background px-2 text-sm"
                    >
                        {SAML_PROVIDERS.map((p) => (
                            <option key={p} value={p}>{PROVIDER_HINTS[p].label}</option>
                        ))}
                    </select>
                    <Input
                        placeholder='Display name — e.g. "Okta (Production)"'
                        value={newDisplayName}
                        onChange={(e) => setNewDisplayName(e.target.value)}
                    />
                    <Button onClick={handleCreate} disabled={create.isPending || !newDisplayName.trim()}>
                        {create.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                        Add
                    </Button>
                </div>
                {create.isError ? <p className="mt-2 text-xs text-destructive">{create.error?.message}</p> : null}
            </section>

            <div className="grid grid-cols-[260px_1fr] gap-4">
                <aside className="rounded-lg border bg-card p-2">
                    {idps.isPending ? (
                        <p className="p-3 text-sm text-muted-foreground">Loading…</p>
                    ) : (idps.data ?? []).length > 0 ? (
                        <div className="flex flex-col gap-0.5">
                            {idps.data!.map((p) => (
                                <button
                                    key={p.id}
                                    type="button"
                                    onClick={() => setSelectedId(p.id)}
                                    className={`flex items-center justify-between rounded-md px-3 py-2 text-left text-sm hover:bg-accent ${
                                        selectedId === p.id ? "bg-accent" : ""
                                    }`}
                                >
                                    <div className="min-w-0 flex-1">
                                        <div className="truncate font-medium">{p.displayName}</div>
                                        <div className="truncate text-xs text-muted-foreground">{PROVIDER_HINTS[p.provider].label}</div>
                                    </div>
                                    <StatusBadge idp={p} />
                                </button>
                            ))}
                        </div>
                    ) : (
                        <p className="p-3 text-sm text-muted-foreground">No identity providers registered yet.</p>
                    )}
                </aside>

                {selected ? (
                    <IdpDetail key={selected.id} tenantId={tenantId} idp={selected} onDeleted={() => setSelectedId(null)} />
                ) : (
                    <section className="flex items-center justify-center rounded-lg border bg-card p-10 text-sm text-muted-foreground">
                        Select a registration to configure and validate it.
                    </section>
                )}
            </div>
        </div>
    );
}

function StatusBadge({ idp }: { idp: SamlIdentityProvider }) {
    if (idp.enabled) {
        return <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-medium text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200">enabled</span>;
    }
    if (idp.lastValidationError) {
        return <span className="rounded-full bg-destructive/10 px-2 py-0.5 text-[10px] font-medium text-destructive">error</span>;
    }
    if (idp.lastValidatedAt) {
        return <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">validated</span>;
    }
    return <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">unvalidated</span>;
}

function IdpDetail({
    tenantId,
    idp,
    onDeleted
}: {
    tenantId: string;
    idp: SamlIdentityProvider;
    onDeleted: () => void;
}) {
    const update = useUpdateSamlIdp(tenantId);
    const validate = useValidateSamlIdp(tenantId);
    const remove = useDeleteSamlIdp(tenantId);

    // Keyed on idp.id via the parent's `key` prop, so no reset effect needed.
    const [displayName, setDisplayName] = useState(idp.displayName);
    const [buttonLabel, setButtonLabel] = useState(idp.buttonLabel);
    const [metadataSource, setMetadataSource] = useState<"url" | "paste">(idp.metadataUrl ? "url" : "paste");
    const [metadataUrl, setMetadataUrl] = useState(idp.metadataUrl ?? "");
    const [metadataXml, setMetadataXml] = useState(idp.metadataXml ?? "");
    const [emailClaim, setEmailClaim] = useState(idp.emailClaim);
    const [autoProvisionUsers, setAutoProvisionUsers] = useState(idp.autoProvisionUsers);
    const [enabled, setEnabled] = useState(idp.enabled);

    const dirty =
        displayName !== idp.displayName ||
        buttonLabel !== idp.buttonLabel ||
        (metadataSource === "url" ? metadataUrl : "") !== (idp.metadataUrl ?? "") ||
        (metadataSource === "paste" ? metadataXml : "") !== (idp.metadataXml ?? "") ||
        emailClaim !== idp.emailClaim ||
        autoProvisionUsers !== idp.autoProvisionUsers ||
        enabled !== idp.enabled;

    // lastValidationError arrives as `undefined` (not `null`) when the BFF's
    // WhenWritingNull JSON option omits it — a strict `=== null` check would
    // never pass. Falsy covers null/undefined/empty-string uniformly.
    const canEnable = !idp.lastValidationError && idp.lastValidatedAt != null;

    const daysLeft = idp.idpCertificateExpiresAt
        ? Math.floor((new Date(idp.idpCertificateExpiresAt).getTime() - Date.now()) / 86_400_000)
        : null;

    const handleSave = () => {
        update.mutate({
            id: idp.id,
            payload: {
                provider: idp.provider,
                displayName,
                buttonLabel: buttonLabel || null,
                metadataUrl: metadataSource === "url" ? (metadataUrl.trim() || null) : null,
                metadataXml: metadataSource === "paste" ? (metadataXml.trim() || null) : null,
                emailClaim: emailClaim.trim() || null,
                autoProvisionUsers,
                enabled
            }
        });
    };

    return (
        <section className="space-y-4">
            <SpMetadataCard id={idp.id} />

            <div className="rounded-lg border bg-card p-4 shadow-sm">
                <h3 className="mb-3 text-sm font-semibold">{PROVIDER_HINTS[idp.provider].label} registration</h3>

                <div className="grid gap-3 sm:grid-cols-2">
                    <label className="flex flex-col gap-1 text-xs">
                        <span className="font-medium text-muted-foreground">Display name</span>
                        <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
                    </label>
                    <label className="flex flex-col gap-1 text-xs">
                        <span className="font-medium text-muted-foreground">Sign-in button label</span>
                        <Input value={buttonLabel} onChange={(e) => setButtonLabel(e.target.value)} placeholder={`Continue with ${idp.provider}`} />
                    </label>
                </div>

                <div className="mt-3">
                    <div className="mb-1 flex items-center gap-3">
                        <span className="text-xs font-medium text-muted-foreground">Metadata source</span>
                        <label className="flex items-center gap-1 text-xs">
                            <input type="radio" checked={metadataSource === "url"} onChange={() => setMetadataSource("url")} />
                            URL
                        </label>
                        <label className="flex items-center gap-1 text-xs">
                            <input type="radio" checked={metadataSource === "paste"} onChange={() => setMetadataSource("paste")} />
                            Paste XML
                        </label>
                    </div>
                    {metadataSource === "url" ? (
                        <Input
                            value={metadataUrl}
                            onChange={(e) => setMetadataUrl(e.target.value)}
                            placeholder={PROVIDER_HINTS[idp.provider].metadataUrlHint}
                        />
                    ) : (
                        <textarea
                            className="min-h-[120px] w-full rounded-md border bg-background px-3 py-2 font-mono text-xs"
                            value={metadataXml}
                            onChange={(e) => setMetadataXml(e.target.value)}
                            placeholder="Paste the IdP's metadata XML here"
                        />
                    )}
                </div>

                <label className="mt-3 flex flex-col gap-1 text-xs">
                    <span className="font-medium text-muted-foreground">Email claim</span>
                    <Input value={emailClaim} onChange={(e) => setEmailClaim(e.target.value)} placeholder={EMAIL_CLAIM_DEFAULT} />
                    <span className="text-[10px] text-muted-foreground">
                        Which attribute on the assertion carries the user's email address. Only
                        change this if the IdP sends it under a different claim URI.
                    </span>
                </label>

                <label className="mt-3 flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={autoProvisionUsers} onChange={(e) => setAutoProvisionUsers(e.target.checked)} />
                    Auto-provision users on first sign-in
                </label>
                <p className="-mt-1 text-[10px] text-muted-foreground">
                    When off, only an already-existing user (matched by email) can sign in through
                    this provider — a new email is refused rather than auto-created.
                </p>

                <label className="mt-2 flex items-center gap-2 text-sm">
                    <input
                        type="checkbox"
                        checked={enabled}
                        disabled={!canEnable}
                        onChange={(e) => setEnabled(e.target.checked)}
                    />
                    Enabled
                    {!canEnable ? (
                        <span className="text-xs text-muted-foreground">— validate successfully first</span>
                    ) : null}
                </label>

                <div className="mt-4 flex items-center justify-between">
                    <div className="flex gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => validate.mutate(idp.id)}
                            disabled={validate.isPending || (!idp.metadataUrl && !idp.metadataXml)}
                        >
                            {validate.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                            Validate
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            className="text-destructive"
                            onClick={() => {
                                if (window.confirm(`Delete "${idp.displayName}"?`)) {
                                    remove.mutate(idp.id, { onSuccess: onDeleted });
                                }
                            }}
                        >
                            <Trash2 className="h-4 w-4" /> Delete
                        </Button>
                    </div>
                    <Button size="sm" disabled={!dirty || update.isPending} onClick={handleSave}>
                        {update.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                        Save
                    </Button>
                </div>
                {update.isError ? <p className="mt-2 text-xs text-destructive">{update.error?.message}</p> : null}
            </div>

            <div className="rounded-lg border bg-card p-4 shadow-sm">
                <h3 className="mb-3 text-sm font-semibold">Last validation</h3>
                {idp.lastValidationError ? (
                    <div className="mb-3 flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-2 text-xs text-destructive">
                        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                        {idp.lastValidationError}
                    </div>
                ) : null}
                {idp.lastValidatedAt ? (
                    <dl className="grid grid-cols-[120px_1fr] gap-y-1 text-xs">
                        <dt className="text-muted-foreground">Validated</dt>
                        <dd>{new Date(idp.lastValidatedAt).toLocaleString()}</dd>
                        <dt className="text-muted-foreground">IdP entity ID</dt>
                        <dd className="truncate"><code>{idp.idpEntityId}</code></dd>
                        <dt className="text-muted-foreground">SSO URL</dt>
                        <dd className="truncate"><code>{idp.idpSsoUrl}</code> {idp.idpSsoBinding ? `(${idp.idpSsoBinding})` : ""}</dd>
                        <dt className="text-muted-foreground">Certificate expires</dt>
                        <dd className={daysLeft !== null && daysLeft < CERT_WARNING_DAYS ? "text-amber-600" : undefined}>
                            {idp.idpCertificateExpiresAt ? new Date(idp.idpCertificateExpiresAt).toLocaleDateString() : "—"}
                            {daysLeft !== null && daysLeft < CERT_WARNING_DAYS ? ` (${daysLeft}d left)` : ""}
                        </dd>
                    </dl>
                ) : !idp.lastValidationError ? (
                    <p className="text-xs text-muted-foreground">Not validated yet.</p>
                ) : null}
            </div>
        </section>
    );
}
